"use client";

import { CreditCard } from "lucide-react";

import { Button, buttonVariants } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { PricingPlan } from "@/types/dashboard.types";
import { plans } from "../data/pricing.data";
import { useUpdateCredits } from "../queries/conversions.update.queries";

export function AddCredits() {
  return (
    <div className="flex justify-center">
      <Dialog>
        <DialogTrigger asChild>
          <Button className="w-full" size={"lg"} id="pricing-dialog">
            View Pricing Plans
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">
              Choose Your Plan
            </DialogTitle>
            <DialogDescription className="text-center">
              Select the plan that works best for your needs.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-6 py-4 md:grid-cols-3">
            {plans.map((plan) => (
              <PricingCard key={plan.name} plan={plan} />
            ))}
          </div>
          <BusinessPlanCard />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function BusinessPlanCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Custom Plan</CardTitle>
        <CardDescription className="text-base">
          Have unique needs or higher volume requirements? Let’s talk. Contact
          us to create a tailored plan that fits your institution’s specific
          goals.
        </CardDescription>
      </CardHeader>
      <CardFooter className="justify-end">
        <a
          href="mailto:support@transcript.ai"
          className={cn(buttonVariants({ variant: "link" }), "underline")}
        >
          Contact Us
        </a>
      </CardFooter>
    </Card>
  );
}

function PricingCard({ plan }: { plan: PricingPlan }) {
  const { mutateAsync: updateCredits, isPending } = useUpdateCredits();

  return (
    <Card
      className={cn(
        "flex flex-col",
        plan.popular && "border-primary shadow-md"
      )}
    >
      {plan.popular && (
        <div className="absolute -top-3 left-0 right-0 mx-auto w-fit rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
          Most Popular
        </div>
      )}
      <CardHeader className={cn("pb-8")}>
        <CardTitle className="text-xl">{plan.name}</CardTitle>
        <CardDescription>
          <span className="text-3xl font-bold">${plan.price}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="flex-1">
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-muted p-3">
          <CreditCard className="h-5 w-5 text-muted-foreground" />
          <span className="font-medium">{plan.credits} transcripts</span>
        </div>
      </CardContent>
      <CardFooter>
        <Button
          className={cn(
            "w-full cursor-pointer",
            plan.popular ? "bg-primary" : "bg-primary/90"
          )}
          onClick={() => updateCredits(plan.credits)}
          disabled={isPending}
        >
          {isPending ? "Processing..." : "Select " + plan.name}
        </Button>
      </CardFooter>
    </Card>
  );
}
